import React, { useState, useRef, useContext, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  RequestPhotoMessage as RequestPhotoMessageType,
  RetakePhotoMessage,
} from '@goodrx/heydoctor-sdk/es/models/message';
import { MessageTypes, TaskStatuses, VisitStatuses } from '@goodrx/heydoctor-core';
import { styled, Button, Flex, Text, Icon } from '@goodrx/matisse';
import { WebcamModal } from 'app/components/core/WebcamModal';
import { isMobileOrTablet } from 'app/lib/utils';
import { DefaultMessage, MessageProps } from 'app/components/widgets/chat/message-types';
import { useSynapse } from 'app/lib/synaptik';
import { resizePhoto } from 'app/routes/visit/tasks/utils/photoUtils';
import { trackEvent } from 'app/lib/analytics';
import * as Sentry from '@sentry/browser';
import { AppContext } from 'app/config/AppContext';

const UploadedText = styled(Text)`
  background-color: ${(p) => p.theme.colors.greyLight};
  border-radius: ${(p) => p.theme.space[2]}px;
  color: ${(p) => p.theme.colors.green};
  font-weight: 600;
  padding: 10px 12px;
  text-align: center;
`;

const MessageActionButton = styled(Button)`
  height: 40px;
`;

interface RequestPhotoMessageProps extends MessageProps {
  message: RequestPhotoMessageType | RetakePhotoMessage;
}

export const RequestPhotoMessage: React.FC<RequestPhotoMessageProps> = ({ message, messageRef }) => {
  const { isGold } = useContext(AppContext);
  const history = useHistory();

  const [
    fetchVisit,
    fetchHistory,
    visits,
    uploadPhoto,
    markPhotoRequestAsCompleted,
    hasPatientRespondedToPhotoRequest,
  ] = useSynapse(({ visits, chat }) => [
    visits.fetch,
    visits.fetchHistory,
    visits.state.visits,
    visits.uploadPhoto,
    chat.markPhotoRequestAsCompleted,
    chat.hasPatientRespondedToPhotoRequest,
  ]);

  const [isWebcamOpen, setIsWebcamOpen] = useState(false);

  const [isVisitActive, setIsVisitActive] = useState(true);

  const [image, setImage] = useState<Blob | string>();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Conditional check is needed to support retake requests sent before https://github.com/heydoctor/api/pull/1721
  // Going forward all retake requests should have a photo associated with them.
  const isLegacyRetakeMessage = useMemo(() => message.type === MessageTypes.RETAKE_PHOTOS && !message.photo, [message]);

  useEffect(() => {
    if (!visits.length) {
      fetchHistory();
    } else {
      const visit = visits.find((v) => v.id === message.visitId);
      if (
        visit &&
        visit.status !== VisitStatuses.READY_FOR_PROVIDER &&
        visit.status !== VisitStatuses.PRE_VISIT_INTAKE
      ) {
        setIsVisitActive(false);
      }
    }
  }, [visits, fetchHistory, message.visitId]);

  useEffect(() => {
    if (isLegacyRetakeMessage) return;
    if (hasPatientRespondedToPhotoRequest(message)) {
      markPhotoRequestAsCompleted(message.id);
    }
  }, [isLegacyRetakeMessage, message, markPhotoRequestAsCompleted, hasPatientRespondedToPhotoRequest]);

  const handleUpload = async (photoData: Blob | string) => {
    setImage(photoData);
    setIsUploading(true);
    try {
      await fetchVisit(message.visitId);
      await uploadPhoto(message.visitPhotoId, photoData);
      markPhotoRequestAsCompleted(message.id);
      setUploadFailed(false);
      trackEvent('Photo Upload Complete', { photo: message.photo.photoType.title });
    } catch (e) {
      setUploadFailed(true);
      Sentry.captureException(e);
      trackEvent('Photo Upload Failed', { photo: message.photo.photoType.title });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = () => {
    if (!fileInputRef.current) return;
    fileInputRef.current.addEventListener(
      'change',
      async () => {
        if (!fileInputRef.current || !fileInputRef.current?.files || !fileInputRef.current.files?.[0]) return;
        const file = fileInputRef.current.files[0];
        try {
          // try to resize and upload file
          const photoData = await resizePhoto({ file });
          await handleUpload(photoData);
        } catch (e) {
          // in case of error upload file as is
          Sentry.captureException(e);
          const reader = new FileReader();
          reader.onload = (e) => e.target && typeof e.target.result === 'string' && handleUpload(e.target.result);
          reader.readAsDataURL(file);
        }
      },
      false
    );
    fileInputRef.current.click();
  };

  const handleTakePhoto = () => {
    if (isGold) {
      window.open(`goodrx://gth_retake_photo?visit_id=${message.visitId}`);
      return;
    }
    if (isMobileOrTablet() && fileInputRef.current) {
      handleFileUpload();
    } else {
      setIsWebcamOpen(true);
    }
  };

  const handleRetry = () => {
    trackEvent('Photo Upload Retry', { photo: message.photo.photoType.title });
    handleUpload(image!);
  };

  const handleCancel = () => {
    trackEvent('Photo Upload Canceled', { photo: message.photo.photoType.title });
    setUploadFailed(false);
    setImage(undefined);
  };

  const getUiBasedOnPhotoStatus = () => {
    const isAwaitingInitialUpload = message.photo.status === TaskStatuses.OPEN;
    const isImageRejected = message.photo.status === TaskStatuses.REJECTED;
    const isImageCancelled = message.photo.status === TaskStatuses.CANCELLED;

    if (uploadFailed) {
      return (
        <>
          <Flex pl={1} pr={1} mt={3}>
            <MessageActionButton
              block
              data-qa={`${isUploading ? 'uploading_image' : 'retry_upload_btn'}`}
              onClick={handleRetry}
              loading={isUploading}
              mr={2}>
              Retry
            </MessageActionButton>
            <MessageActionButton
              block
              variant="secondary"
              data-qa="cancel_btn"
              onClick={handleCancel}
              disabled={isUploading}>
              Cancel
            </MessageActionButton>
          </Flex>
          <Text as="p" ml={1} color="red">
            <Icon name="alert-circle" color="red" style={{ marginRight: '4px' }} />
            Photo upload failed.
          </Text>
        </>
      );
    }
    if (isImageRejected || isImageCancelled || isAwaitingInitialUpload) {
      const buttonText = isImageRejected ? 'Retake Photo' : 'Take Photo';
      const dataQa = isImageRejected ? 'retake_photo_btn' : 'take_photo_btn';
      const eventText = isImageRejected
        ? "Request Photo Message 'Retake Photo' Button clicked"
        : "Request Photo Message 'Take Photo' Button clicked";

      return (
        <MessageActionButton
          block
          data-qa={`${isUploading ? 'uploading_image' : dataQa}`}
          onClick={() => {
            trackEvent(eventText);
            handleTakePhoto();
          }}
          mt={3}
          loading={isUploading}
          disabled={isImageCancelled || !isVisitActive}>
          {buttonText}
        </MessageActionButton>
      );
    }
    return (
      <UploadedText as="p" data-qa="uploaded-text">
        Uploaded
      </UploadedText>
    );
  };

  const handleLegacyRetakeMessageClick = () => {
    if (isGold) {
      window.open(`goodrx://gth_retake_photo?visit_id=${message.visitId}`);
    } else {
      // Can not use `/visits/${message.visitId}/photos` because visits will not be reloaded
      // Missing slug is used by the visits component to reload the visit.
      history.push(`/visits/${message.visitId}`);
    }
  };

  if (isLegacyRetakeMessage) {
    return (
      <DefaultMessage message={message} messageRef={messageRef} fromCurrentUser={false}>
        <MessageActionButton block mt={3} onClick={handleLegacyRetakeMessageClick} disabled={!isVisitActive}>
          Retake Photo
        </MessageActionButton>
      </DefaultMessage>
    );
  }

  const { patientDisplayName } = message.photo.photoType;

  const messageTitle = `Photo Requested${patientDisplayName === 'Other' ? '' : `: ${patientDisplayName}`}`;

  return (
    <>
      <WebcamModal
        onRequestClose={() => setIsWebcamOpen(false)}
        onSelect={handleUpload}
        open={isWebcamOpen}
        imageTitle={patientDisplayName === 'Other' ? undefined : patientDisplayName}
      />
      <DefaultMessage message={message} messageRef={messageRef} fromCurrentUser={false} messageTitle={messageTitle}>
        {getUiBasedOnPhotoStatus()}
      </DefaultMessage>
      <input ref={fileInputRef} type="file" capture="environment" accept="image/*" style={{ display: 'none' }} />
    </>
  );
};
